import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  TextField,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import EditIcon from '@material-ui/icons/Edit';
import { API } from 'aws-amplify';
import { getAPIName } from '../../config';

export interface AutocompleteOption {
  label: string;
  value: any;
  data?: any;
  [key: string]: any;
}


export interface AutocompleteSelectFlags {
  enableAutocomplete?: boolean;
  enableMultiSelect?: boolean;
  showEditButton?: boolean;
  showAddButton?: boolean;
  useAutocompleteUI?: boolean;
  useAPIData?: boolean;
  sortOptions?: boolean;
  clearOnSelect?: boolean;
  disabled?: boolean;
}

/**
 * Styling options for customization
 */
export interface AutocompleteSelectStyles {
  width?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  padding?: string;
  paddingTop?: string;
}

/**
 * Main props interface
 */
export interface AutocompleteSelectProps {
  /** Options to display (used when useAPIData is false) */
  options?: AutocompleteOption[];
  apiPath?: string;
  keySelector?: string;
  /** Value/display selector for mapping API response (default: 'name') */
  valueSelector?: string;
  additionalSelector?: string;
  value?: any | any[];
  onChange?: (value: any | any[] | null) => void;
  onInitialLoad?: (value: any) => void;
  onInputChange?: (event: any, value: string, reason: string) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  styles?: AutocompleteSelectStyles;
  flags?: AutocompleteSelectFlags;
  showBreaks?: boolean;
  refreshTrigger?: any;
  addButton?: {
    label: string;
    value?: string;
  };

  /** Modal for adding new options */
  addModal?: (props: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
  }) => JSX.Element;

  /** Modal for editing options */
  editModal?: (props: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    option: AutocompleteOption;
  }) => JSX.Element;
  inputProps?: any;
  className?: string;
  variant?: 'standard' | 'filled' | 'outlined';
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  formControl: {
    width: '100%',
  },
  autocompleteContainer: {
    width: '100%',
  },
  autocompleteInput: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  editButton: {
    padding: theme.spacing(0.5),
  },
  optionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  addOptionLabel: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
}));

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  options = [],
  apiPath,
  keySelector = 'id',
  valueSelector = 'name',
  additionalSelector,
  value,
  onChange,
  onInitialLoad,
  onInputChange,
  label,
  placeholder,
  error,
  helperText,
  styles,
  flags = {},
  refreshTrigger,
  addButton,
  addModal,
  editModal,
  inputProps,
  className,
  variant = 'standard',
  fullWidth = true,
  size = 'medium',
  showBreaks = true,
}) => {
  const classes = useStyles();
  const [loadedOptions, setLoadedOptions] = useState<AutocompleteOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<AutocompleteOption[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingOption, setEditingOption] = useState<AutocompleteOption | null>(null);

  // Flag defaults
  const {
    enableAutocomplete = false,
    enableMultiSelect = false,
    showEditButton = !!editModal,
    showAddButton = !!addModal,
    useAutocompleteUI = true,
    useAPIData = !!apiPath,
    sortOptions = true,
    clearOnSelect = false,
    disabled = false,
  } = flags;

  const addOptionValue = addButton?.value || '__add_option__';

  // Fetch options from API if enabled - only refetch on API-related changes, NOT on value changes
  useEffect(() => {
    const fetchOptions = async () => {
      if (!useAPIData || !apiPath) {
        // Use static options
        const transformed = transformOptions(options);
        setLoadedOptions(transformed);
        return;
      }
      try {
        const response: any = await API.get(getAPIName(), apiPath, {});
        const items = Array.isArray(response.body) ? response.body : response.body || [];
        const transformed = items
          .map((item: any) => ({
            label: item[additionalSelector || valueSelector] || item[valueSelector],
            value: item[keySelector],
            data: item,
          } as AutocompleteOption))
          .filter((opt: AutocompleteOption) => opt.label); // Filter out null labels

        if (sortOptions) {
          transformed.sort((a: AutocompleteOption, b: AutocompleteOption) => a.label.localeCompare(b.label));
        }

        setLoadedOptions(transformed);
      } catch (error) {
        console.error('Failed to load options from API:', error);
      }
    };

    fetchOptions();
  }, [apiPath, keySelector, valueSelector, additionalSelector, useAPIData, refreshTrigger, sortOptions]);

  // Sync selected option when loadedOptions or value changes - SEPARATE from API fetch
  useEffect(() => {
    syncSelectedOption(loadedOptions, value);
  }, [value, loadedOptions]);

  // Handle static options updates
  useEffect(() => {
    if (!useAPIData) {
      const transformed = transformOptions(options);
      setLoadedOptions(transformed);
    }
  }, [options, useAPIData, sortOptions]);

  // Transform static options to AutocompleteOption format
  const transformOptions = (opts: AutocompleteOption[]): AutocompleteOption[] => {
    let transformed = [...opts];
    if (sortOptions) {
      transformed.sort((a, b) => a.label.localeCompare(b.label));
    }
    return transformed;
  };

  // Sync selected option(s) with value prop
  const syncSelectedOption = (opts: AutocompleteOption[], newValue: any) => {
    if (newValue === undefined || newValue === null) {
      setSelectedOption(null);
      setSelectedOptions([]);
      return;
    }

    if (enableMultiSelect) {
      const values = Array.isArray(newValue) ? newValue : [newValue];
      const matched = opts.filter((o) => values.includes(o.value));
      setSelectedOptions(matched);
    } else {
      const matched = opts.find((o) => o.value === newValue);
      setSelectedOption(matched || null);
    }
  };

  // Handle selection change
  const handleAutocompleteChange = (_: any, newValue: AutocompleteOption | AutocompleteOption[] | null) => {
    if (Array.isArray(newValue)) {
      // Multi-select case
      if (newValue.some((opt) => opt.value === addOptionValue)) {
        setOpenAddModal(true);
        return;
      }
      setSelectedOptions(newValue);
      onChange?.(newValue.map((opt) => opt.value));
    } else {
      // Single select case
      if (newValue?.value === addOptionValue) {
        setOpenAddModal(true);
        return;
      }
      setSelectedOption(newValue);
      onChange?.(newValue?.value ?? null);

      if (clearOnSelect && newValue) {
        setTimeout(() => {
          setSelectedOption(null);
          onChange?.(null);
        }, 300);
      }
    }
  };

  // Handle Material-UI Select change
  const handleSelectChange = (event: any) => {
    const newValue = event.target.value;

    if (enableMultiSelect) {
      const values = typeof newValue === 'string' ? newValue.split(',').map(Number) : newValue;
      onChange?.(values);
    } else {
      onChange?.(newValue || null);
    }
  };

  // Handle edit click
  const handleEditClick = (e: React.MouseEvent, option: AutocompleteOption) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingOption(option);
    setOpenEditModal(true);
  };

  // Prepare options for rendering
  const displayOptions = addButton
    ? [
        {
          label: addButton.label,
          value: addOptionValue,
          data: null,
        },
        ...loadedOptions,
      ]
    : loadedOptions;

  // Custom styles
  const customStyles = {
    width: styles?.width || '100%',
    backgroundColor: styles?.backgroundColor,
    color: styles?.textColor,
    borderRadius: styles?.borderRadius,
    padding: styles?.padding,
    paddingTop: styles?.paddingTop || '5px'
  };

  // Use Autocomplete component if enabled
  if (useAutocompleteUI && enableAutocomplete) {
    return (
      <div className={className}>
        <FormControl style={{ width: '100%' }}>
        {showBreaks && <><br /><br /></>}
        <Autocomplete
          multiple={enableMultiSelect}
          options={displayOptions}
          value={enableMultiSelect ? selectedOptions : selectedOption}
          onChange={handleAutocompleteChange}
          onInputChange={onInputChange}
          getOptionLabel={(option: AutocompleteOption) => option.label}
          filterOptions={(options: AutocompleteOption[], state: any) => {
            // Custom filter for autocomplete
            return options.filter((option: AutocompleteOption) =>
              option.label.toLowerCase().includes(state.inputValue.toLowerCase())
            );
          }}
          renderOption={(option: AutocompleteOption) => (
            option.value === addOptionValue ? (
              <span className={classes.addOptionLabel}>{option.label}</span>
            ) : (
              <div className={classes.optionContainer}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.label}</span>
                {showEditButton && editModal && (
                  <IconButton
                    size="small"
                    className={classes.editButton}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => handleEditClick(e, option)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            )
          )}
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              variant={variant}
              error={error}
              helperText={helperText}
              {...inputProps}
            />
          )}
          renderTags={(value: any, getTagProps: any) =>
            enableMultiSelect
              ? value.map((option: AutocompleteOption, index: number) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    className={classes.chip}
                  />
                ))
              : []
          }
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          style={customStyles}
          noOptionsText="No options"
          loadingText="Loading..."
        />

        {addModal?.({
          open: openAddModal,
          onClose: () => setOpenAddModal(false),
          onSuccess: () => {
            setOpenAddModal(false);
            // Trigger refresh by refetching options
            if (useAPIData && apiPath) {
              // Component will refetch due to refreshTrigger or manual refresh
            }
          },
        })}

        {editingOption &&
          editModal?.({
            open: openEditModal,
            onClose: () => {
              setOpenEditModal(false);
              setEditingOption(null);
            },
            onSuccess: () => {
              setOpenEditModal(false);
              setEditingOption(null);
              // Trigger refresh
            },
            option: editingOption,
          })}
        </FormControl>
      </div>
    );
  }

  // Use Material-UI Select component (no autocomplete)
  return (
    <div className={className}>
      <FormControl fullWidth={fullWidth} error={error} className={classes.formControl}>
        {label && <InputLabel>{label}</InputLabel>}
        <Select
          value={enableMultiSelect ? (Array.isArray(value) ? value : []) : value ?? ''}
          onChange={handleSelectChange}
          disabled={disabled}
          multiple={enableMultiSelect}
          displayEmpty
          variant={variant as any}
          {...inputProps}
          style={customStyles}
        >
          {!enableMultiSelect && (
            <MenuItem value="" disabled>
              {placeholder || 'Select an option'}
            </MenuItem>
          )}
          {displayOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.value === addOptionValue ? (
                <span className={classes.addOptionLabel}>{option.label}</span>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{option.label}</span>
                  {showEditButton && editModal && (
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => handleEditClick(e, option)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              )}
            </MenuItem>
          ))}
        </Select>
        {helperText && <span style={{ color: error ? '#f44336' : 'inherit' }}>{helperText}</span>}
      </FormControl>

      {addModal?.({
        open: openAddModal,
        onClose: () => setOpenAddModal(false),
        onSuccess: () => {
          setOpenAddModal(false);
        },
      })}

      {editingOption &&
        editModal?.({
          open: openEditModal,
          onClose: () => {
            setOpenEditModal(false);
            setEditingOption(null);
          },
          onSuccess: () => {
            setOpenEditModal(false);
            setEditingOption(null);
          },
          option: editingOption,
        })}
    </div>
  );
};

export default AutocompleteSelect;
